import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plane, CreditCard, Calendar, Shield, Clock, CheckCircle2, TrendingDown, DollarSign, Users, ArrowRight, Lightbulb, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';

export default function FintechInstallmentFlightBooking() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Comparison table data
  const comparisonData = [
    {
      provider: 'Southwest (Flex Pay by Upgrade)',
      structure: 'Fixed monthly installments',
      interest: '0%–36% APR, depending on credit; terms shown at checkout',
      features: 'Book directly with the airline; soft credit check; $150 minimum purchase'
    },
    {
      provider: 'Paylater Travel (Australia)',
      structure: 'Deposit from 5%, then weekly or fortnightly payments over up to 6 months',
      interest: 'No interest or late fees; cancellation fees apply',
      features: 'No credit check; flights confirmed after the final payment'
    },
    {
      provider: 'Affirm',
      structure: 'Pay in 4 (bi-weekly) or monthly terms',
      interest: '0%–36% APR; Pay in 4 is 0%',
      features: 'Soft check to see your plans; no late fees'
    },
    {
      provider: 'Klarna',
      structure: 'Four equal payments every two weeks or monthly up to 24 months',
      interest: 'Pay in 4 is interest-free; financing up to 35.99% APR',
      features: 'App-based budgeting and reminders'
    },
    {
      provider: 'Defera',
      structure: 'Deposit, then weekly or bi-weekly payments',
      interest: 'No interest; fees included in the trip total',
      features: 'No credit check; trip price secured once your booking is approved'
    }
  ];

  // Payment schedule table data
  const scheduleData = [
    {
      plan: 'Pay in 4 (Klarna/Affirm)',
      frequency: 'Bi-weekly (4 payments over ~6 weeks)',
      interest: 'No',
      autoPay: 'Yes'
    },
    {
      plan: 'Monthly installments (Affirm/Klarna)',
      frequency: 'Monthly (3–24 months; Affirm up to 60)',
      interest: 'Yes (often on longer terms)',
      autoPay: 'Yes'
    },
    {
      plan: 'Weekly installments (Paylater Travel, Australia)',
      frequency: 'Weekly or fortnightly (up to 6 months)',
      interest: 'No (other fees may apply)',
      autoPay: 'Yes'
    },
    {
      plan: 'Weekly or bi-weekly plan (Defera)',
      frequency: 'Weekly or bi-weekly, finishing before you fly',
      interest: 'No',
      autoPay: 'Yes'
    }
  ];

  // FAQ data
  const faqs = [
    {
      question: 'What installment plan options are available for flights?',
      answer: 'The main options include airline plans, third-party BNPL providers, and multi-airline platforms, letting you pay in fixed monthly, bi-weekly, or weekly installments with clear terms.'
    },
    {
      question: 'How do I book a flight using an installment plan?',
      answer: 'Search for your flight, choose an installment option at checkout, complete the quick approval, and pay any required upfront amount. Your itinerary is secured, and the balance is paid over time.'
    },
    {
      question: 'Are there interest charges or fees with flight installment plans?',
      answer: 'Many pay-in-4 plans are 0% with no hidden fees if paid on time. Longer terms may include interest, so review total costs and due dates before you confirm.'
    },
    {
      question: 'Who is eligible to use flight installment financing?',
      answer: 'Typically, adults 18+ with a valid payment method qualify, with fast approvals and often only a soft credit check.'
    },
    {
      question: 'Is booking flights with installment plans safe and reliable?',
      answer: 'Reputable providers show the full schedule and total cost before you commit and honor your booking when payments are made as agreed, making installment plans a safe, practical option for most travelers.'
    }
  ];

  return (
    <>
      <PageSeo
        path="/guides/fintech-installment-flight-booking"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Best Fintech Options for Booking Flights on Installments",
            "description": "Compare top fintech platforms and airline plans for installment flight payments, including how to secure fares with deposits and manage flexible schedules.",
            "author": {
              "@type": "Organization",
              "name": "Defera"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Defera",
              "logo": {
                "@type": "ImageObject",
                "url": "https://defera.app/defera-transparent.png"
              }
            },
            "datePublished": "2024-12-12",
            "dateModified": "2026-11-03",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://defera.app/guides/fintech-installment-flight-booking"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          },
        ]}
      />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link 
              to="/guides" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero Section */}
        <section className="pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-white/45 text-xs font-normal uppercase tracking-wider">
                  Travel Guide
                </span>
                <span className="text-white/40 text-xs">
                  Updated November 2026
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                How to Overcome Flight Booking Costs with Easy Installment Plans
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Flying doesn't have to mean draining your savings in one go. Installment-based flight booking spreads airfare across predictable payments so you can lock in fares today and pay over time—often with zero interest and clear schedules.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Key Points Summary */}
        <section className="pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#1d1d1f] border border-white/[0.08] rounded-[1.25rem] p-6 md:p-8"
            >
              <p className="text-white/80 leading-relaxed">
                The best fintech comes down to your priorities: instant approvals, transparent pricing, or widest airline access. Below, we break down the options, how they work, and how to choose a plan that fits your budget. <strong className="text-white">Defera’s approach centers on weekly or bi-weekly plans with no interest and no credit check</strong> that put you in control, but we also outline leading airline and third-party alternatives so you can pick with confidence.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section: Understanding Flight Booking Installment Plans */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Understand Flight Booking Installment Plans
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                A flight booking installment plan lets you secure airfare now and repay the cost over set intervals—weekly, bi-weekly, or monthly—rather than paying everything upfront. These <strong className="text-white">installment flights</strong>, sometimes referred to as <strong className="text-white">buy now pay later flights</strong> or <strong className="text-white">flight financing</strong>, make travel more accessible by aligning payments with real-world cash flow.
              </p>
              <p className="text-white/80 leading-relaxed mb-6">
                Beyond affordability, the benefits are practical:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80">You can book last-minute or spontaneous trips without a large lump sum.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80">You keep more cash free for luggage, seats, or on-trip expenses.</span>
                </li>
              </ul>
              <p className="text-white/80 leading-relaxed mb-6">
                Rising demand is driven by interest-free options, transparent schedules, and clear, upfront pricing, as consumer guides highlight across providers and marketplaces like <a href="https://www.skyscanner.com" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white underline underline-offset-2">Skyscanner's BNPL overview</a>.
              </p>
              
              {/* Tip Box */}
              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 flex items-start gap-4">
                <Lightbulb className="w-5 h-5 text-white/70 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-white/80 text-sm">
                  <strong className="text-white/95">Tip:</strong> Many platforms now emphasize interest-free "Pay in 4" options and upfront clarity on longer-term APRs, reinforcing trust and predictability.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section: Research Available Options */}
        <section className="py-12 px-6 bg-zinc-950/50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Research Available Flight Payment Options
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Both airlines and independent fintechs offer installment solutions. For example, <strong className="text-white">Southwest</strong> offers Flex Pay by Upgrade (formerly Uplift), which splits fares into monthly payments with terms shown at checkout. <strong className="text-white">Klarna</strong> often supports four interest-free payments every two weeks or longer terms up to 24 months, while travel marketplaces such as <strong className="text-white">Alternative Airlines</strong> enable installments across 600+ airlines using partners like Affirm, PayPal, Afterpay, and Klarna.
              </p>

              {/* Comparison Table */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4">Comparison at a glance:</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Provider</th>
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Installment structure</th>
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Interest/fees</th>
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Unique features</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((row, index) => (
                        <tr key={index} className={`border-b border-white/10 ${row.provider === 'Defera' ? 'bg-white/[0.04]' : ''}`}>
                          <td className={`py-3 px-4 text-sm ${row.provider === 'Defera' ? 'text-white font-medium' : 'text-white/90'}`}>
                            {row.provider}
                          </td>
                          <td className="py-3 px-4 text-white/80 text-sm">{row.structure}</td>
                          <td className="py-3 px-4 text-white/80 text-sm">{row.interest}</td>
                          <td className="py-3 px-4 text-white/80 text-sm">{row.features}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Evidence Box */}
              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 space-y-4">
                <h4 className="text-white font-medium text-sm flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-white/70" strokeWidth={1.5} />
                  Evidence and examples:
                </h4>
                <ul className="space-y-3 text-sm text-white/80">
                  <li>• Southwest’s Flex Pay page describes Flex Pay as a buy now, pay later product by Upgrade, with monthly installments and terms shown at checkout. See Southwest’s program details for specifics.</li>
                  <li>• "Klarna splits ticket prices into four equal interest-free payments every two weeks, or monthly installments up to 24 months," according to a leading consumer travel guide.</li>
                  <li>• Skyscanner's BNPL guide notes that Alternative Airlines supports installments on 600+ airlines with major BNPL partners, expanding your choice set.</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section: Choosing the Right Financing Service */}
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Choose the Right Financing Service
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                The best fintech for booking flights in installments depends on what you value most:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <Clock className="w-5 h-5 text-white/70 mb-3" strokeWidth={1.5} />
                  <h4 className="text-white font-semibold mb-2">Speed</h4>
                  <p className="text-white/80 text-sm">Instant approvals help you secure limited fares fast.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <Calendar className="w-5 h-5 text-white/70 mb-3" strokeWidth={1.5} />
                  <h4 className="text-white font-semibold mb-2">Flexibility</h4>
                  <p className="text-white/80 text-sm">More choices in cadence (weekly, bi-weekly, monthly) make budgeting easier.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <Shield className="w-5 h-5 text-white/70 mb-3" strokeWidth={1.5} />
                  <h4 className="text-white font-semibold mb-2">Transparency</h4>
                  <p className="text-white/80 text-sm">Clear schedules and straightforward policies reduce surprises.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <Users className="w-5 h-5 text-white/70 mb-3" strokeWidth={1.5} />
                  <h4 className="text-white font-semibold mb-2">Support</h4>
                  <p className="text-white/80 text-sm">Responsive customer service matters if your plans change.</p>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white mb-4">Direct airline plans vs. independent platforms:</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Plane className="w-5 h-5 text-white/40 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80"><strong className="text-white">Airline plans</strong> (e.g., Flex Pay by Upgrade on Southwest) keep everything at the airline’s checkout with predictable monthly installments.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-white/40 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80"><strong className="text-white">Independent platforms</strong> cover many airlines, offer varied cadences, and can include features like deposits and price locks. For instance, Paylater Travel in Australia lets you pay weekly or fortnightly over up to six months after a deposit of at least 5%, while Defera offers a deposit and then weekly or bi-weekly payments, with no credit check, no interest, and a trip total that already includes every fee.</span>
                </li>
              </ul>

              {/* Quick Shortlist */}
              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-6 mb-6">
                <h4 className="text-white font-semibold mb-4">Quick shortlist by need:</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-white/80"><strong className="text-white">Need speed and automation:</strong> Choose providers with instant approvals.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-white/80"><strong className="text-white">Want predictable costs and no credit check:</strong> Opt for services with no interest and no credit check (e.g., Defera).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="text-white/80"><strong className="text-white">Desire widest airline coverage:</strong> Use marketplaces supporting 600+ airlines via multiple BNPL partners.</span>
                  </li>
                </ul>
              </div>

              <h4 className="text-white font-semibold mb-3">Before you commit, compare:</h4>
              <ul className="space-y-2 text-white/80 text-sm mb-6">
                <li>• Approval requirements and credit checks</li>
                <li>• Late-fee policy and hardship support</li>
                <li>• Minimum and maximum repayment periods</li>
                <li>• Whether autopay is available and easy to switch on</li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Section: Lock in Your Fare */}
        <section className="py-12 px-6 bg-zinc-950/50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Lock in Your Fare with a Deposit
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Fare locking secures a ticket price for a limited time with no large deposits or minimal upfront payment, protecting you if prices rise before you finish paying. Some services let you lock in now and spread the remainder across installments, combining peace of mind with budget control.
              </p>

              <h3 className="text-lg font-semibold text-white mb-4">Typical flow:</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                {[
                  { step: '1', text: 'Find your desired flight' },
                  { step: '2', text: 'Choose deposit or price lock at checkout' },
                  { step: '3', text: 'Pay the deposit' },
                  { step: '4', text: 'Confirm the remaining installment schedule and due dates' }
                ].map((item, index) => (
                  <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-4 text-center">
                    <div className="w-8 h-8 rounded-full bg-white/[0.08] text-white/90 flex items-center justify-center mx-auto mb-2 text-sm font-medium">
                      {item.step}
                    </div>
                    <p className="text-white/80 text-sm">{item.text}</p>
                  </div>
                ))}
              </div>

              <p className="text-white/80 leading-relaxed">
                Booking with installment plans can help you <strong className="text-white">lock in today's prices</strong> and avoid potential fare hikes while you complete your payments, particularly on platforms that support deposit-based price holds.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section: Set Up and Manage Your Payment Schedule */}
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Set Up and Manage Your Payment Schedule
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Reduce stress by automating and visualizing your plan:
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80"><strong className="text-white">Turn on autopay</strong> where available; major providers like Affirm and Klarna support auto-payments to help you avoid missed due dates.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80"><strong className="text-white">Confirm your end date and total cost</strong> before you agree, especially for longer terms.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80"><strong className="text-white">Keep room in your budget</strong> for currency conversion or ancillary fees if you're booking international flights.</span>
                </li>
              </ul>

              {/* Payment Schedule Table */}
              <h3 className="text-lg font-semibold text-white mb-4">Common schedules and features:</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-white font-medium text-sm">Payment plan</th>
                      <th className="text-left py-3 px-4 text-white font-medium text-sm">Frequency</th>
                      <th className="text-left py-3 px-4 text-white font-medium text-sm">Interest (Y/N)</th>
                      <th className="text-left py-3 px-4 text-white font-medium text-sm">Auto-pay supported</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleData.map((row, index) => (
                      <tr key={index} className={`border-b border-white/10 ${row.plan.includes('Defera') ? 'bg-white/[0.04]' : ''}`}>
                        <td className={`py-3 px-4 text-sm ${row.plan.includes('Defera') ? 'text-white font-medium' : 'text-white/90'}`}>
                          {row.plan}
                        </td>
                        <td className="py-3 px-4 text-white/80 text-sm">{row.frequency}</td>
                        <td className="py-3 px-4 text-white/80 text-sm">{row.interest}</td>
                        <td className="py-3 px-4 text-white/80 text-sm">{row.autoPay}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section: Budget Effectively */}
        <section className="py-12 px-6 bg-zinc-950/50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Budget Effectively for Your Installments
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                A few small habits make installment flights seamless:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80"><strong className="text-white">Track your plan</strong> in a budgeting app or simple spreadsheet so every due date is visible alongside other bills.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80"><strong className="text-white">Add calendar reminders</strong> or connect an expense manager that syncs with your bank for real-time alerts.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="text-white/80">Breaking airfare into installments helps you <strong className="text-white">budget monthly expenses</strong> and avoid a large lump-sum hit.</span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-white mb-4">Benefits you'll feel:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-start gap-4">
                  <TrendingDown className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Lower stress</h4>
                    <p className="text-white/80 text-sm">By spreading costs across pay cycles.</p>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-start gap-4">
                  <DollarSign className="w-6 h-6 text-white/70 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold mb-1">More cash free</h4>
                    <p className="text-white/80 text-sm">For trip extras or unforeseen expenses.</p>
                  </div>
                </div>
              </div>

              <p className="text-white/80 leading-relaxed mb-6">
                To time your purchase, use price alerts (e.g., Google Flights price tracking or Skyscanner price alerts), which email you when a fare changes.
              </p>

              {/* CTA Box */}
              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-6">
                <p className="text-white/80 leading-relaxed">
                  If you want weekly or bi-weekly payments with no interest and no credit check, and a trip price that’s secured once your booking is approved, <strong className="text-white">explore Defera’s traveler-first approach</strong>.
                </p>
                <Button
                  onClick={() => navigate('/search')}
                  className="mt-4 bg-white hover:bg-white/90 text-black px-6 py-2 rounded-full font-semibold text-sm"
                >
                  Search Flights with Defera
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl px-6 py-5 hover:border-white/[0.12] transition-colors">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to book your next flight?
              </h2>
              <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl mx-auto">
                Experience the Defera difference: no credit check, no interest, and weekly or bi-weekly payment plans.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={() => navigate('/search')}
                  className="bg-white hover:bg-white/90 text-black px-8 py-6 rounded-full font-semibold text-base w-full sm:w-auto"
                >
                  Search Flights
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="text-white/60 hover:text-white text-sm font-medium transition-colors"
                >
                  Learn how it works →
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-white/40 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-white/55" strokeWidth={2} />
                  <span>No credit check</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-white/55" strokeWidth={2} />
                  <span>0% interest</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-white/55" strokeWidth={2} />
                  <span>Major airlines worldwide</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Related guides</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/guides/flight-loans-vs-payment-plans" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight loans vs payment plans
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/airlines-that-offer-buy-now-pay-later" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Airlines that offer buy now, pay later
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-layaway-vs-bnpl" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight layaway vs BNPL: what’s the difference?
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/lock-flight-price-with-deposit" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  How to lock in a flight price with a deposit
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
}

